// =============================================================================
//  Copyright : 2018 semantics GmbH
//  Date      : $Date: 2021-03-03 15:41:20 +0100 (Wed, 03 Mar 2021) $
//  Author    : Dirk Rothe
//  Worker    : $Author: d.rothe $
//  Revision  : $Rev: 117483 $
//  Purpose   : debug utilities
// =============================================================================
// ---Third-party imports
import React, {Component, Fragment} from 'react'
import PropTypes from 'prop-types'

import {Dropdown, Icon, Button} from 'semantic-ui-react'
import now from 'performance-now'

// ---Framework imports
import log from 'uix/logging'
import styles from './viewer.scss'
import {
  range,
  each,
  flatten,
  isEqual,
  get,
  sum,
  includes,
  size,
  values,
  mapItems,
  cloneDeep,
} from 'ldash'
import {Layout, Draw} from './layouts'

import connect from 'uix/connect'
import {selectRootDomain, selectId} from 'uix/selectors'
import {Actions} from 'uix/registries'
import {defaultOptions} from './options'

// ---Globals

let wheelModeOptionLabels = [
  {
    text: 'Zoom',
    value: 'zoom',
  },
  {
    text: 'PanX',
    value: 'panX',
  },
  {
    text: 'PanY',
    value: 'panY',
  },
]

let scrollToOptionLabels = [
  {
    text: 'Scroll',
    value: 'scroll',
  },
  {
    text: 'Zoom',
    value: 'zoom',
  },
  {
    text: 'Native',
    value: 'native',
  },
  {
    text: 'Auto',
    value: 'auto',
  },
  {
    text: 'Jump',
    value: 'jump',
  },
]

let alignOptionLabels = [
  {
    text: 'Square',
    value: 'square',
  },
  {
    text: 'Hor',
    value: 'horizontal',
  },
  {
    text: 'Ver',
    value: 'vertical',
  },
  {
    text: 'Tree',
    value: 'tree',
  },
  {
    text: 'Hor2',
    value: 'horizontal.2',
  },
  {
    text: 'Hor21',
    value: 'horizontal.2.1',
  },
  {
    text: 'Hor4',
    value: 'horizontal.4',
  },
  {
    text: 'Hor41',
    value: 'horizontal.4.1',
  },
  {
    text: 'Ver2',
    value: 'vertical.2',
  },
  {
    text: 'Ver21',
    value: 'vertical.2.1',
  },
  {
    text: 'Ver4',
    value: 'vertical.4',
  },
  {
    text: 'Ver42',
    value: 'vertical.4.2',
  },
]

let alignTransOptionLabels = [
  {
    text: 'Off',
    value: 0.0,
  },
  {
    text: '0.2',
    value: 0.2,
  },
  {
    text: '0.5',
    value: 0.5,
  },
  {
    text: '1.0',
    value: 1.0,
  },
  {
    text: '2.0',
    value: 2.0,
  },
]

let placeholderOptionLabels = [
  {
    text: 'Keine',
    value: 'none',
  },
  {
    text: '128er',
    value: 'thumb',
  },
  {
    text: 'Auto',
    value: 'autoWidth',
  },
  {
    text: 'Info',
    value: 'info',
  },
]

let renderOptionLabels = [
  {
    text: 'Images',
    value: 'images',
  },
  {
    text: 'Info',
    value: 'info',
  },
]

let paginationOptionLabels = [
  {
    text: 'Off',
    value: 'off',
  },
  {
    text: 'Fluid',
    value: 'fluid',
  },
]

let TrueFalseLabels = [
  {
    text: 'True',
    value: 'true',
  },
  {
    text: 'False',
    value: 'false',
  },
]

let IconSlider = ({icon, docs, onChange, ...props}) => (
  <div className={styles.viewerSliderBox} title={docs}>
    <Icon name={icon} onClick={e => onChange(props.value + (e.ctrlKey || e.shiftKey ? 1 : -1))} />
    <input
      className={styles.viewerSlider}
      type="range"
      onChange={event => onChange(event.target.value)}
      {...props}
    />
  </div>
)

let LabelDropdown = ({label, options, onLabelClick, ...props}) => {
  return (
    <div>
      <span onClick={onLabelClick}>{`${label}: `}</span>
      <Dropdown inline options={options} {...props} />
    </div>
  )
}

let LabelChoices = ({choices, uiOpts, path, onChange, setOption, ...props}) => {
  if (!onChange) {
    onChange = (e, d) => setOption(path, d.value)
  }

  let nextChoice = () => {
    let current = get(uiOpts, path)
    let values = choices.map(e => e.value)
    let i = values.indexOf(current)
    let next = values[(i + 1) % values.length]
    if (onChange) {
      onChange({}, {value: next})
    } else {
      setOption(path, next)
    }
  }

  return (
    <LabelDropdown
      defaultValue={get(uiOpts, path)}
      options={choices}
      onChange={onChange}
      onLabelClick={nextChoice}
      {...props}
    />
  )
}

let LabelBool = ({uiOpts, path, setOption, ...props}) => {
  return (
    <LabelDropdown
      defaultValue={get(uiOpts, path).toString()}
      options={TrueFalseLabels}
      onChange={(e, d) => setOption(path, d.value == 'true')}
      onLabelClick={() => setOption(path, get(uiOpts, path).toString() != 'true')}
      {...props}
    />
  )
}

let DemoButton = ({...props}) => <div style={{cursor: 'pointer'}} {...props} />

let BoxHeader = ({text}) => <h5 style={{marginTop: 0, marginBottom: 2}}>{text}</h5>

let Indicator = ({values}) => {
  let style = v => ({
    paddingLeft: '2px',
    paddingRight: '2px',
    backgroundColor: v ? '#7cb342' : '',
    fontWeight: 'bold',
  })
  let panels = mapItems(values, (k, v) => {
    return (
      <span key={k} style={style(v)}>
        {k}
      </span>
    )
  })
  return <div style={{width: '100px'}}>{panels}</div>
}

let titleChooser = (rootDomain, currentId) => {
  let res = []
  switch (rootDomain) {
    case 'erara':
      res = [
        {text: 'Small', value: 15509339},
        {text: 'DuoPage', value: 201},
        {text: 'Map', value: 15135489},
        {text: 'Large', value: 9149},
      ]
      break
    case 's2wp':
      res = [{text: 'VW', value: 3}, {text: 'Map-8', value: 415380}, {text: 'Map-1', value: 415420}]
      break
  }

  if (res.filter(e => e.value == currentId).length == 0) {
    res.push({text: 'Random', value: currentId})
  }

  return res
}

let ScrollAndDims = ({scrollWidth, scrollHeight, zlw, zlh, animating}) => {
  scrollWidth = parseInt(scrollWidth)
  scrollHeight = parseInt(scrollHeight)
  zlw = parseInt(zlw)
  zlh = parseInt(zlh)

  let style = {}
  let title = 'vcol reports this size'
  if (!animating && (scrollWidth != zlw || scrollHeight != zlh)) {
    style = {backgroundColor: '#FAA'}
    title = `should be equal to calculated dims: ${zlw}x${zlh}`
  }
  return <div title={title} style={style}>{`Scroll: ${scrollWidth}x${scrollHeight}`}</div>
}

class _DebugPane extends React.Component {
  static uiDefaults = {
    opts: defaultOptions,
    __version__: defaultOptions.__version__,
  }

  static storeMap = {
    rootDomain: selectRootDomain,
    rootId: selectId,
  }
  static actionMap = {go: Actions.go, goRandomId: Actions.goRandomId}

  constructor(props) {
    super(props)
    this.state = {evadeLayout: null}
  }

  static getDerivedStateFromProps(nextProps, prevState) {
    if (!prevState.initialized) {
      //~ log.warn('init with %o', nextProps.ui.opts)
      //~ nextProps.viewer.setState({opts: nextProps.ui.opts})
      //~ nextProps.viewer.setLayout()
      return {initialized: true}
    }
    return null
  }

  _getVisibleIds = (overscan = 0) => {
    let {layout, viewport} = this.props.viewer.state

    return layout.bush
      .search({
        minX: viewport.left - overscan,
        minY: viewport.top - overscan,
        maxX: viewport.right + overscan,
        maxY: viewport.bottom + overscan,
      })
      .map(e => e.id)
  }

  _getCenterId = () => {
    let {layout, viewport} = this.props.viewer.state

    return get(
      layout.bush.search({
        minX: viewport.left + viewport.width / 2.1,
        minY: viewport.top + viewport.height / 2.1,
        maxX: viewport.right - viewport.width / 2.1,
        maxY: viewport.bottom - viewport.height / 2.1,
      })[0],
      'id',
      null
    )
  }

  setLimit = limit => {
    //~ let {pages, options} = this.props.viewer.state.layout
    //~ let layout = Layout(pages, {...options, limit})
    //~ this.setLayout(layout)
    this.setOption('layout.limit', limit)
  }

  setLayout = layout => this.props.viewer.setLayout(layout)

  setAlign = align => {
    let opts = this.props.ui.opts
    this.props.viewer.realign(align)
    this.props.setUI({opts: {...opts, layout: {...opts.layout, align}}})
    //~ this.setOption('layout.align', align)
  }

  moveDemo = () => {
    let {viewer} = this.props
    let {center, layout, animating, zoom, viewport, opts} = viewer.state
    let {width, height, areas} = layout
    // special case: we are already in a animation
    // center from state is probably not calculated
    // so use raw data
    if (animating && this.props.viewerFrame) {
      // XXX: this does not handle FAST_PATH
      let {x, y} = this.props.viewerFrame
      center = {x, y} // zoom is always correct
    }

    //~ let randomPoint = {x: width * Math.random(), y: height * Math.random()}
    let randomArea = areas[parseInt((areas.length - 1) * Math.random())]
    //~ log.info('goto %o', randomArea)

    if (opts.layout.spatialIndex) {
      let centerId = this._getCenterId()
      let distance = opts.scrollTo.mode == 'scroll' ? 4 : 3 // 4 or 3 pages overscan
      let visibles = this._getVisibleIds(distance).filter(e => e != centerId)
      let rid = visibles[parseInt((visibles.length - 1) * Math.random())]

      randomArea = layout.byId[rid]
    }

    let zoomOpts = {}

    let zoom_one_page_width = zoom * (viewport.right - viewport.left)

    let SHRINK = 0.6
    if (opts.scrollTo.mode == 'zoom') {
      //~ log(zoom_one_page_width, zoom)
      zoomOpts = {
        startZoom: zoom,
        endZoom: Math.max(
          128,
          parseInt(zoom_one_page_width * SHRINK) - parseInt(Math.random() * 800)
        ),
      }
      if (opts.scrollTo.mode == 'fly') {
        zoomOpts.mode = 'dive'
      }
    }

    let endCenter = {x: randomArea.center.x, y: randomArea.center.y - randomArea.height / 6}
    if (opts.scrollTo.mode == 'native') {
      let endZoom = Math.max(
        128,
        parseInt(zoom_one_page_width * SHRINK) - parseInt(Math.random() * 800)
      )

      viewer.scrollNative(endCenter.x, endCenter.y, endZoom)
    } else {
      viewer.smoothScroll({
        start: center,
        // move a little bit up from center
        end: endCenter,
        duration: 'auto',
        ...zoomOpts,
      })
    }
  }

  morphDemo = mode => {
    let {viewer} = this.props
    let {center, layout, animating, zoom, viewport, opts} = viewer.state
    //~ let {width, height} = layout
    if (mode == 'pump') {
      // ommitting endLayout gives pump effect
      viewer.morph({
        //~ startZoom: zoom,
        //~ endZoom: parseInt(zoom * 1.5),
        startLayout: layout,
        duration: 5,
      })
    } else if (mode == 'killSome') {
      let killedIds = layout.pages.filter(e => Math.random() < 0.2).map(e => e.id)

      let filteredPages = layout.pages.filter(e => !killedIds.includes(e.id))
      let endLayout = Layout(filteredPages, {...layout.options})

      // modify layout inplace, let's see how this works out
      Draw(layout).remove(killedIds)

      viewer.morph({
        start: center,
        end: endLayout.center,
        startLayout: layout,
        endLayout,
        duration: 2,
      })
    } else {
      log.error('unknown mode: %s', mode)
    }
  }

  sequentialDemo = () => {
    let {viewer} = this.props
    let {center, layout, zoom, viewport, opts} = viewer.state

    viewer.smoothScrollSeq({
      start: center,
      startZoom: zoom,
      endZoom: parseInt(15 + Math.random() * 100),
      duration: 1,
      sequenceStart: true,
    })

    viewer.smoothScrollSeq({endZoom: 2000, duration: 3})
    viewer.smoothScrollSeq({endZoom: 128, duration: 2})
    viewer.smoothScrollSeq({endZoom: 1000, duration: 2})
    viewer.smoothScrollSeq({endZoom: 500, duration: 1})
    for (let i of range(20)) {
      viewer.smoothScrollSeq({endZoom: 550 + 50 * i, duration: 0.1})
    }
    for (let i of range(20)) {
      viewer.smoothScrollSeq({endZoom: 2000 - 50 * i, duration: 0.1})
    }
  }

  setupEvasionDemo = start => {
    let {registerHandler, state} = this.props.viewer
    if (start) {
      registerHandler({onCursorMove: this.cursorEvadeDemo})
      this.setState({evadeLayout: cloneDeep(state.layout)})
    } else {
      registerHandler({onCursorMove: null})
      this.setState({evadeLayout: null})
    }
  }

  cursorEvadeDemo = ({x, y}) => {
    let {evadeLayout} = this.state
    let {layout} = this.props.viewer.state
    if (
      evadeLayout.options.align != layout.options.align &&
      (!layout.animationId || layout.animationLast)
    ) {
      log.debug('clone new evasion layout')
      evadeLayout = cloneDeep(layout)
      this.setState({evadeLayout})
    }

    let xlayout = cloneDeep(evadeLayout)
    //~ let layout = cloneDeep(this.props.layout)

    let transformer = area => {
      let MIN_SPACE = 0.5
      let EASE_OFF = 1.5
      let {x: cx, y: cy} = area.center
      let inv_dist = 1 / ((cx - x) ** 2 + (cy - y) ** 2) ** 0.5
      inv_dist = Math.min(MIN_SPACE, inv_dist ** EASE_OFF)
      let delta_x = x > cx ? -inv_dist : inv_dist
      let delta_y = y > cy ? -inv_dist : inv_dist

      return {...area, x: area.x + delta_x, y: area.y + delta_y}
    }

    Draw(xlayout).transformAreas(transformer)
    //~ log('%o', layout.areas[0].x)
    this.setLayout(xlayout)
  }

  getSelectionInfo = () => {
    let {selected, layout} = this.props.viewer.state
    let id = selected.hover || selected.center
    if (id) {
      let area = layout.byId[id]
      if (area) {
        return `w${area.orgWidth}, l${area.page.level}`
      } else {
        return '??'
      }
    } else {
      return '?'
    }
  }

  setOption = (name, value) => {
    let {setUI, viewer} = this.props
    let opts = viewer.setOption(name, value)
    setUI({opts})
  }

  switchId = id => {
    if (id) {
      this.props.go({id})
    } else {
      this.props.goRandomId()
    }
  }

  render() {
    if (!this.state.initialized) {
      return null
    }

    let {viewer} = this.props
    let frameDelays = viewer._frameinfo.delays

    let {setZoom, setCenter, setAnimating, registerHandler} = viewer

    let {
      zoom,
      opts,
      animating,
      scrollInfo,
      sizeInfo,
      center,
      viewport,
      cursor,
      selected,
      layout,
      mode,
      zlayout,
    } = viewer.state
    //~ opts = this.props.ui.opts

    let {ui, setUI, rootDomain, rootId, _recomputes_, _panerrenders_} = this.props

    let setOption = this.setOption

    let {width: zlw, height: zlh} = zlayout
    let {scrollLeft, scrollTop, scrollWidth, scrollHeight} = scrollInfo

    // XXX: unclear Hacks with Math.random()
    // but initialization

    return (
      <div className={styles.viewerControls}>
        <IconSlider
          icon="zoom"
          docs="change zoom"
          min={5}
          max={150}
          value={parseInt(zoom ** (1 / 1.7))}
          onChange={zoom => setZoom(parseInt(zoom ** 1.7))}
        />
        <IconSlider
          icon="arrow right"
          docs="scroll horizontal"
          className={styles.viewerSlider}
          max={layout.width * 100}
          value={center.x * 100}
          onChange={value => setCenter(value / 100, center.y)}
        />
        <IconSlider
          icon="arrow down"
          docs="scroll vertical"
          className={styles.viewerSlider}
          max={layout.height * 100}
          value={center.y * 100}
          onChange={value => setCenter(center.x, value / 100)}
        />
        <IconSlider
          icon="compress"
          docs="change gutter"
          className={styles.viewerSlider}
          max={100}
          value={parseInt(opts.layout.gutter_x * 100)}
          onChange={value =>
            setOption(['layout.gutter_x', 'layout.gutter_y'], [value / 100, value / 100])
          }
        />
        <IconSlider
          icon="minus square"
          docs="limit number of pages"
          className={styles.viewerSlider}
          min={0}
          max={layout.pages.length}
          value={opts.layout.limit}
          onChange={this.setLimit}
        />
        <IconSlider
          icon="lightning"
          docs="frames per second"
          className={styles.viewerSlider}
          min={6}
          max={80}
          value={opts.fps}
          onChange={fps => setOption('fps', fps)}
        />
        <div style={{marginLeft: '5px'}}>
          <div className={styles.viewerBox}>
            <BoxHeader text="State" />
            <Indicator
              values={{
                A: animating,
                I: mode == 'initializing',
                Z: mode == 'zooming',
                S: mode == 'scrolling',
                M: mode == 'moving',
                R: mode == 'resizing',
              }}
            />
            <div title={`FPS: ${opts.fps}: ${(1000 / opts.fps).toFixed(1)} ms`}>{`Lag: ${(
              sum(frameDelays.map(e => Math.abs(e))) / frameDelays.length
            ).toFixed(1)} ms`}</div>
            <div>{`Computes: ${_recomputes_}`}</div>
            <div>{`Renders: ${_panerrenders_}`}</div>
            <div>{`LayoutId: ${layout.id}`}</div>
          </div>

          <div className={styles.viewerBox}>
            <BoxHeader text="Demo" />
            <div>
              <span onClick={() => this.switchId(null)}>Title </span>
              <Dropdown
                inline
                defaultValue={parseInt(rootId)}
                options={[...titleChooser(rootDomain, parseInt(rootId))]}
                onChange={(e, d) => this.switchId(d.value)}
                renderLabel={({text}) => 'YYYXXX'}
              />
            </div>

            <DemoButton onClick={() => this.moveDemo()}>
              <span>Move </span>
              <Dropdown
                inline
                defaultValue={opts.scrollTo.mode}
                options={scrollToOptionLabels}
                onChange={(e, d) => setOption('scrollTo.mode', d.value)}
              />
            </DemoButton>
            <DemoButton onClick={() => this.morphDemo('pump')}>Pump</DemoButton>
            <DemoButton onClick={() => this.morphDemo('killSome')}>KillSome</DemoButton>
            <DemoButton onClick={() => this.sequentialDemo('inOut')}>inOut</DemoButton>
            <div>
              <span>Evade </span>
              <Dropdown
                inline
                defaultValue={this.state.evadeLayout ? 'true' : 'false'}
                options={TrueFalseLabels}
                onChange={(e, d) => this.setupEvasionDemo(d.value == 'true')}
              />
            </div>
          </div>

          <div className={styles.viewerBox}>
            <BoxHeader text="Mouse" />
            <LabelBool label="On" uiOpts={opts} path={'mouse.on'} setOption={setOption} />
            <div>{`Pos: ${cursor.position.x.toFixed(0)}x${cursor.position.y.toFixed(0)}`}</div>
            <div>{`VP: ${cursor.x.toFixed(2)}x${cursor.y.toFixed(2)}`}</div>
            <Indicator
              values={{
                M: cursor.detectedEnvironment.isMouseDetected,
                T: cursor.detectedEnvironment.isTouchDetected,
                O: cursor.isPositionOutside,
                D: cursor.isDragging,
              }}
            />
            <LabelBool
              label="DrgScroll"
              uiOpts={opts}
              path={'mouse.dragScroll'}
              setOption={setOption}
            />
            <div>
              <span>WhlZoom </span>
              <Dropdown
                inline
                defaultValue={opts.mouse.wheelMode}
                options={wheelModeOptionLabels}
                onChange={(e, d) => setOption('mouse.wheelMode', d.value)}
              />
            </div>
          </div>

          <div className={styles.viewerBox}>
            <BoxHeader text="Selection" />
            <LabelBool label="On" uiOpts={opts} path={'selection.on'} setOption={setOption} />
            <div>{`Center: ${selected.center}`}</div>
            <div>{`Hover: ${selected.hover}`}</div>
            <div>{`Info: ${this.getSelectionInfo()}`}</div>
          </div>

          <div className={styles.viewerBox}>
            <BoxHeader text="Layout" />
            <div>{`Items: ${layout.pages.length} [${zlayout.areas.length}]`}</div>
            <div>{`Limit: ${opts.layout.limit}`}</div>
            <LabelChoices
              label="Align"
              uiOpts={opts}
              path={'layout.align'}
              choices={alignOptionLabels}
              onChange={(e, d) => this.setAlign(d.value)}
              compact={true}
            />
            <LabelChoices
              label="Transition"
              uiOpts={opts}
              path={'layout.alignTransition'}
              choices={alignTransOptionLabels}
              setOption={setOption}
            />
            <LabelBool
              label="Bush"
              uiOpts={opts}
              path={'layout.spatialIndex'}
              setOption={setOption}
            />
            <div>{`Gutter: ${layout.options.gutter_x.toFixed(2)}`}</div>
            <LabelBool
              label="DPI-Aware"
              uiOpts={opts}
              path={'layout.dpiAware'}
              setOption={setOption}
            />
          </div>

          <div className={styles.viewerBox}>
            <BoxHeader text="Viewport" />
            <div
              title="image width on screen = zoom value"
              onClick={() => setZoom(128)}
            >{`Zoom: ${zoom.toFixed(1)}`}</div>
            <div title="visible bbox size">{`Client: ${sizeInfo.width}x${sizeInfo.height}`}</div>
            <ScrollAndDims
              scrollWidth={scrollWidth}
              scrollHeight={scrollHeight}
              zlw={zlw}
              zlh={zlh}
              animating={animating}
            />
            <div>{`LeftTop: ${parseInt(scrollLeft)}x${parseInt(scrollTop)}`}</div>
            <div title="center coords in pixels">{`Center: ${parseInt(
              scrollLeft + sizeInfo.width / 2
            )}x${parseInt(scrollTop + sizeInfo.height / 2)}`}</div>
            <div
              onClick={() => setCenter(layout.areas[0].center.x, layout.areas[0].center.y)}
            >{`VP-C: ${center.x.toFixed(2)}x${center.y.toFixed(2)}`}</div>
            <div>{`VP-LT: ${viewport.left.toFixed(2)}x${viewport.top.toFixed(2)}`}</div>
            <div>{`VP-RB: ${viewport.right.toFixed(2)}x${viewport.bottom.toFixed(2)}`}</div>
          </div>

          <div className={styles.viewerBox}>
            <BoxHeader text="Quality" />
            <LabelBool
              label="Retina"
              uiOpts={opts}
              path={'quality.devicePixelRatio'}
              setOption={setOption}
            />
            <LabelChoices
              label="Placeholder"
              path={'quality.placeholder'}
              choices={placeholderOptionLabels}
              uiOpts={opts}
              setOption={setOption}
            />
            <LabelChoices
              label="Render"
              path={'render.mode'}
              choices={renderOptionLabels}
              uiOpts={opts}
              setOption={setOption}
            />
            <LabelChoices
              label="Pagination"
              path={'render.paginations'}
              choices={paginationOptionLabels}
              uiOpts={opts}
              setOption={setOption}
            />
            <LabelBool label="Tiles" uiOpts={opts} path={'layout.tiles'} setOption={setOption} />
          </div>
        </div>
      </div>
    )
  }
}
let DebugPane = connect(
  'ViewerDebugPane',
  _DebugPane
)
export {DebugPane}
